import type { RefObject } from 'react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

/** The size of the observed element. */
export type Size = {
    /** The width of the observed element. */
    width: number | undefined;
    /** The height of the observed element. */
    height: number | undefined;
};

/** The options for the ResizeObserver. */
export type UseResizeObserverOptions<T extends HTMLElement = HTMLElement> = {
    /** The ref of the element to observe. */
    ref: RefObject<T | null>;
    /**
     * When using `onResize`, the hook doesn't re-render on element size changes; it delegates handling to the provided callback.
     * @default undefined
     */
    onResize?: (size: Size) => void;
    /**
     * The box model to use for the ResizeObserver.
     */
    box?: ResizeObserverBoxOptions;
};

const initialSize: Size = {
    width: undefined,
    height: undefined,
};

/**
 * Custom hook that observes the size of an element using the [`ResizeObserver API`](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver).
 *
 * Ported from unmaintained usehooks-ts package in order to add React 19 support.
 *
 * @see {@link https://github.com/juliencrn/usehooks-ts/blob/61949134144d3690fe9f521260a16c779a6d3797/packages/usehooks-ts/src/useResizeObserver/useResizeObserver.ts}
 */
export function useResizeObserver<T extends HTMLElement = HTMLElement>(
    options: UseResizeObserverOptions<T>,
): Size {
    const { ref, box = 'border-box' } = options;
    const [{ width, height }, setSize] = useState<Size>(initialSize);
    const previousSize = useRef<Size>({ ...initialSize });

    const handleResize = useEffectEvent((size: Size) => {
        if (options.onResize) {
            options.onResize(size);
        } else {
            setSize(size);
        }
    });

    useEffect(() => {
        if (!ref.current) {
            return;
        }
        if (typeof window === 'undefined' || !('ResizeObserver' in window)) {
            return;
        }
        const sizeKey = convertBoxModel(box);

        const observer = new ResizeObserver(([entry]) => {
            if (!entry) {
                return;
            }
            const newWidth = entry[sizeKey][0]?.inlineSize;
            const newHeight = entry[sizeKey][0]?.blockSize;

            if (previousSize.current.width === newWidth && previousSize.current.height === newHeight) {
                return;
            }
            previousSize.current = { width: newWidth, height: newHeight };
            handleResize(previousSize.current);
        });

        observer.observe(ref.current, { box });

        return () => {
            observer.disconnect();
        };
    }, [box, ref]);

    return { width, height };
}

type EntryKey = Extract<keyof ResizeObserverEntry, `${string}Size`>;

function convertBoxModel(model: ResizeObserverBoxOptions): EntryKey {
    switch (model) {
        case 'border-box':
            return 'borderBoxSize';
        case 'content-box':
            return 'contentBoxSize';
        case 'device-pixel-content-box':
            return 'devicePixelContentBoxSize';
    }
}
